import {DateTime} from 'luxon';

export const format = (type, data) => {
    switch(type){
        case 'people':
        case 'users':
            return formatPeople(data);
        case 'personCustomFields':
        case 'eventCustomFields':
            return formatCustomFields(data);
        case 'personCustomFieldSections':
        case 'eventCustomFieldSections':
            return formatCustomFieldSections(data);
        case 'addresses':
            return formatAddresses(data);
        case 'events':
            return formatEvents(data);
        case 'calendars':
            return formatCalendars(data);
        case 'locations':
            return formatLocations(data);
        case 'tasks':
            return formatTasks(data);
        case 'taskTeams':
            return formatTaskTeams(data);
        case 'songs':
            return formatSongs(data);
        case 'permissions':
            return formatPermissions(data);
        case 'roles':
            return formatRoles(data);
        case 'roleFolders':
            return formatRoleFolders(data);
        case 'smallGroups':
            return formatSmallGroups(data);
        case 'smallGroupTypes':
            return formatSmallGroupTypes(data);
        case 'links':
            return formatLinks(data);
        case 'quickFilters':
            return formatQuickFilters(data);
        case 'contributionMethods':
            return formatContributionMethods(data);
        case 'contributionFunds':
            return formatContributionFunds(data);
        case 'contributionBatches':
            return formatContributionBatches(data);
        case 'paths':
            return formatPaths(data);
        case 'forms':
            return formatForms(data);
        case 'boards':
            return formatBoards(data);
        case 'trainingModules':
            return formatTrainingModules(data);
        case 'profileViews':
            return formatProfileViews(data);
    }
}

export const sheetIcons = {
    people: "person-outline",
    users: "person-outline",
    personCustomFields: "reader-outline",
    eventCustomFields: "reader-outline",
    personCustomFieldSections: "reader-outline",
    eventCustomFieldSections: "reader-outline",
    addresses: "home-outline",
    events: "calendar-clear-outline",
    calendars: "calendar-outline",
    locations: "location-outline",
    tasks: "clipboard-outline",
    taskTeams: "clipboard-outline",
    songs: "musical-notes-outline",
    permissions: "lock-outline",
    roles: "people-circle-outline",
    roleFolders: "folder-outline",
    smallGroups: "people-outline",
    smallGroupTypes: "people-outline",
    links: "link-outline",  
    quickFilters: "funnel-outline",
    contributionMethods: "card-outline",
    contributionFunds: "wallet-outline",
    contributionBatches: "file-tray-full-outline",
    paths: "git-branch-outline",
    forms: "document-text-outline",
    boards: "grid-outline",
    trainingModules: "school-outline",
    profileViews: "person-circle-outline",
}

// Singular display name per selector type — used for "Create New <name>" actions
// where the list title ("Assignees", "People") would read wrong.
export const sheetTypeNames = {
    people: "Person",
    users: "User",
    personCustomFields: "Custom Field",
    eventCustomFields: "Custom Field",
    personCustomFieldSections: "Field Section",
    eventCustomFieldSections: "Field Section",
    addresses: "Address",
    events: "Event",
    calendars: "Calendar",
    locations: "Location",
    tasks: "Task",
    taskTeams: "Team",
    songs: "Song",
    permissions: "Permission",
    roles: "Role",
    roleFolders: "Folder",
    smallGroups: "Small Group",
    smallGroupTypes: "Group Type",
    links: "Link",
    quickFilters: "Quick Filter",
    contributionMethods: "Contribution Method",
    contributionFunds: "Fund",
    contributionBatches: "Batch",
    paths: "Automation",
    forms: "Form",
    boards: "Board",
    trainingModules: "Training Module",
    profileViews: "Profile View",
}


const formatPeople = (data) => {
    return data?.map((person) => {
        if(!person) return {};
        return {
            id: person.id,
            heading: person.firstName + " " + person.lastName,
            sub_heading: person.Address?.city && person.Address?.state ? person.Address?.city + ", " + person.Address?.state : "",
            body: person.email || "",
            original: person
        }
    });
}

const formatSmallGroupTypes = (data) => {
    return data?.map((type) => {
        return {
            id: type.id,
            heading: type.name,
            original: type
        }
    });
}

const formatCustomFields = (data) => {
    return data?.map((field) => {
        return {
            id: field.id,
            heading: field.name,
            sub_heading: field.Section?.name || "",
            body: "",
            original: field
        }
    });
}


const formatCustomFieldSections = (data) => {
    return data?.map((section) => {
        return {
            id: section.id,
            heading: section.name,
            original: section
        }
    });
}


const formatAddresses = (data) => {
    return data?.map((address) => {
        return {
            id: address.id,
            heading: address.street + (address.streetLine2 ? ", " + address.streetLine2 : ""),
            sub_heading: address.city + ", " + address.state + " " + address.zip,
            body: "",
            original: address
        }
    });
}


const formatEvents = (data) => {
    return data?.map((event) => {
        return {
            id: event.id,
            heading: event.name,
            sub_heading: event.startTime,
            body: event.Calendar?.name,
            original: event
        }
    });
}


const formatCalendars = (data) => {
    return data?.map((calendar) => {
        return {
            id: calendar.id,
            heading: calendar.name,
            sub_heading: calendar.description,
            original: calendar
        }
    });
}


const formatLocations = (data) => {
    return data?.map((location) => {
        return {
            id: location.id,
            heading: location.name,
            sub_heading: location.detailedArea || "",
            body: location.Address ? `${location.Address?.street || ""}, ${location.Address?.city || ""}, ${location.Address?.state || ""} ${location.Address?.zip || ""}` : "",
            original: location
        }
    });
}


const formatTasks = (data) => {
    return data?.map((task) => {
        return {
            id: task.id,
            heading: task.title,
            sub_heading: task.VolunteerTeamTasks?.map(vtt => vtt.VolunteerTeam?.name).filter(Boolean).join(', ') || 'Uncategorized',
            body: task.description,
            original: task
        }
    });
}


const formatSongs = (data) => {
    return data?.map((song) => {
        return {
            id: song.id,
            heading: song.name,
            sub_heading: song.artist,
            original: song
        }
    });
}


const formatPermissions = (data) => {
    return data?.map((permission) => {
        return {
            id: permission.id,
            heading: permission.name,
            sub_heading: permission.description,
            original: permission
        }
    });
}

const formatRoles = (data) => {
    return data?.map((role) => {
        return {
            id: role.id,
            heading: role.name,
            sub_heading: role.description,
            original: role
        }
    });
}

const formatRoleFolders = (data) => {
    const folders = data?.allFolders ? data.allFolders : data;

    return folders?.map((roleFolder) => {
        return {
            id: roleFolder.id,
            heading: roleFolder.name,
            original: roleFolder
        }
    });
}

const formatSmallGroups = (data) => {
    return data?.map((smallGroup) => {
        return {
            id: smallGroup.id,
            heading: smallGroup.name,
            sub_heading: smallGroup.Leader?.firstName ? smallGroup.Leader?.firstName + " " + smallGroup.Leader?.lastName : "",
            body: smallGroup.Students?.length 
                ? smallGroup.Students?.length > 5
                    ? smallGroup.Students.slice(0, 5).map(s => s.Person?.firstName + " " + s.Person?.lastName).join(", ") + " + " + (smallGroup.Students?.length - 5).toString() + " more"
                    : smallGroup.Students?.map(s => s.Person?.firstName + " " + s.Person?.lastName).join(", ") || "" 
                : "No Members",
            original: smallGroup
        }
    });
}


const formatLinks = (data) => {
    return data?.map((link) => {
        return {
            id: link.id,
            heading: link.name,
            sub_heading: link.url,
            original: link
        }
    });
}


const formatTaskTeams = (data) => {
    return data?.map((team) => {
        return {
            id: team.id,
            heading: team.name,
            sub_heading: team.Location?.name || "",
            original: team
        }
    });
}


const formatQuickFilters = (data) => {
    return data?.map((filter) => {
        return {
            id: filter.id,
            heading: filter.name,
            sub_heading: filter.filterJSON ? `${filter.filterJSON?.conditions?.length || 0} conditions` : 'No conditions',
            original: filter
        }
    });
}

const formatContributionMethods = (data) => {
    return data?.map((method) => {
        return {
            heading: method.name,
            id: method.id,
            original: method
        }
    });
}

const formatContributionFunds = (data) => {
    return data?.map((fund) => {
        return {
            id: fund.id,
            heading: fund.name,
            original: fund
        }
    });
}

const formatContributionBatches = (data) => {
    return data?.map((batch) => {
        const totalAmount = Number(batch.totalAmount ?? 0);
        const formattedTotal = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(totalAmount);

        return {
            id: batch.id,
            heading: batch.name ?? batch.batchNumber.toString(),
            sub_heading: DateTime.fromISO(batch.openedAt).toLocaleString(DateTime.DATETIME_MED),
            body: (batch.isOpen ? "Open" : "Closed") + " • " + (batch.transactionCount ?? 0).toString() + " Transactions • " + formattedTotal,
            original: batch
        }
    });
}

const formatPaths = (data) => {
    return data?.map((path) => {
        return {
            id: path.id,
            heading: path.name,
            sub_heading: path.description || '',
            body: (path.stepCount != null ? `${path.stepCount} steps` : '') || '',
            original: path
        }
    });
}

const formatForms = (data) => {
    return data?.map((form) => {
        return {
            id: form.id,
            heading: form.name,
            sub_heading: form.isArchived ? 'Archived' : (form.description || ''),
            original: form
        }
    });
}

const formatBoards = (data) => {
    return data?.map((board) => {
        return {
            id: board.id,
            heading: board.name,
            sub_heading: board.isArchived ? 'Archived' : (board.description || ''),
            original: board
        }
    });
}

const formatTrainingModules = (data) => {
    return data?.map((module) => {
        return {
            id: module.id,
            heading: module.name ?? module.title,
            sub_heading: module.status === 'ARCHIVED' ? 'Archived' : (module.description || ''),
            original: module
        }
    });
}

const formatProfileViews = (data) => {
    return data?.map((view) => {
        return {
            id: view.id,
            heading: view.name,
            sub_heading: view.description || '',
            original: view
        }
    });
}

